import { groupBy, optionalWeightedAvg, percentChange, sum } from "../shared";

const MONTHS_PER_YEAR = 12;
const DAYS_PER_MONTH = 30;
const COVERAGE_HORIZON_MONTHS = 24;
const COVERAGE_HORIZON_DAYS = COVERAGE_HORIZON_MONTHS * DAYS_PER_MONTH;

const calendarMonth = (idx) => ((idx % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR;

const averageSalesByMonth = (rows) =>
  new Map(
    [...groupBy(rows, (row) => calendarMonth(row.idx))].map(([month, monthRows]) => [
      month,
      sum(monthRows, (row) => row.achieved) / monthRows.length,
    ])
  );

const simulateCoverageDays = (stock, salesProfile, startIdx) => {
  if (stock <= 0) return 0;

  let remaining = stock;
  let days = 0;

  for (let offset = 1; offset <= COVERAGE_HORIZON_MONTHS; offset += 1) {
    const month = calendarMonth(startIdx + offset);
    if (!salesProfile.has(month)) continue;

    const sales = salesProfile.get(month);
    if (sales <= 0 || remaining >= sales) {
      if (sales > 0) remaining -= sales;
      days += DAYS_PER_MONTH;
      continue;
    }

    return days + (DAYS_PER_MONTH * remaining) / sales;
  }

  return days;
};

export const enrichCoverageBase = (rows) => {
  const profileByProduct = new Map(
    [...groupBy(rows, (row) => row.productId)].map(([productId, productRows]) => [productId, averageSalesByMonth(productRows)])
  );

  return rows.map((row) => ({ ...row, salesProfile: profileByProduct.get(row.productId) }));
};

export const aggregateCoverage = (rows) => {
  const withInventory = rows.filter((row) => row.inventory !== null);
  if (!withInventory.length) return null;

  const salesProfile = new Map();
  withInventory.forEach((row) =>
    row.salesProfile.forEach((sales, month) => salesProfile.set(month, (salesProfile.get(month) ?? 0) + sales))
  );

  return simulateCoverageDays(sum(withInventory, (row) => row.inventory), salesProfile, withInventory[0].idx);
};

export const enrichTurnoverBase = (rows) => {
  const historyByProduct = new Map();

  rows.forEach((row) => {
    if (!historyByProduct.has(row.productId)) historyByProduct.set(row.productId, new Map());
    historyByProduct.get(row.productId).set(row.idx, row);
  });

  return rows.map((row) => {
    const history = historyByProduct.get(row.productId);
    const at = (offset) => history.get(row.idx - offset);

    const soldWindow = [0, 1, 2].map(at).filter(Boolean);
    const stockWindow = [0, 1, 2, 3].map(at).filter((item) => item && item.inventory !== null);

    return {
      ...row,
      sold3m: sum(soldWindow, (item) => item.achieved),
      avgInventory: stockWindow.length
        ? sum(stockWindow, (item) => item.inventory) / stockWindow.length
        : null,
    };
  });
};

export const aggregateGiro = (rows) => {
  const withStock = rows.filter((row) => row.avgInventory !== null);
  const stock = sum(withStock, (row) => row.avgInventory);
  if (!stock) return null;

  return sum(withStock, (row) => row.sold3m) / stock;
};

export const comparableCapitalChange = (rows, currentIdx, previousIdx) => {
  const stockAt = (idx) => new Map(
    rows.filter((row) => row.idx === idx && row.inventoryCurrency > 0).map((row) => [row.productId, row.inventoryCurrency])
  );
  const currentStock = stockAt(currentIdx);
  const previousStock = stockAt(previousIdx);
  const common = [...currentStock.keys()].filter((productId) => previousStock.has(productId));
  if (!common.length) return null;
  return percentChange(sum(common, (id) => currentStock.get(id)), sum(common, (id) => previousStock.get(id)));
};

const AGE_RANGE_DAYS_PER_WEEK = 7;

const parseAgeRange = (name) => {
  const numbers = (name.match(/\d+/g) ?? []).map(Number);
  if (!numbers.length) return { order: Infinity, maxWeeks: Infinity, openEnded: true, diasLabel: "" };

  const maxWeeks = Math.max(...numbers);
  const days = maxWeeks * AGE_RANGE_DAYS_PER_WEEK;
  const openEnded = name.trim().startsWith(">");
  return { order: numbers[0], maxWeeks, openEnded, diasLabel: openEnded ? `+${days} dias` : `${days} dias` };
};

export const buildStockHealth = (windowRows, currentIdx) => {
  const currentRows = windowRows.filter((row) => row.idx === currentIdx && row.ageRange);
  if (!currentRows.length) return { buckets: [], total: null };

  const rangeByProduct = new Map(currentRows.map((row) => [row.productId, row.ageRange]));
  const salesRows = windowRows.filter((row) => rangeByProduct.has(row.productId));

  const salesByRange = groupBy(salesRows, (row) => rangeByProduct.get(row.productId));
  const stockByRange = groupBy(currentRows, (row) => row.ageRange);

  const rangeNames = [...new Set([...salesByRange.keys(), ...stockByRange.keys()])];
  const totalVenda = sum(salesRows, (row) => row.realized);
  const totalEstoque = sum(currentRows, (row) => row.inventoryCurrency);

  const buckets = rangeNames
    .map((name) => {
      const venda = sum(salesByRange.get(name) ?? [], (row) => row.realized);
      const estoque = sum(stockByRange.get(name) ?? [], (row) => row.inventoryCurrency);

      return {
        key: name,
        label: name,
        ...parseAgeRange(name),
        venda,
        estoque,
        partVenda: totalVenda ? venda / totalVenda : 0,
        partEstoque: totalEstoque ? estoque / totalEstoque : 0,
      };
    })
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, "pt-BR"));

  return {
    buckets,
    total: {
      venda: totalVenda,
      estoque: totalEstoque,
      partVenda: sum(buckets, (bucket) => bucket.partVenda),
      partEstoque: sum(buckets, (bucket) => bucket.partEstoque),
    },
  };
};

const FALLBACK_IDEAL_COVERAGE = [60, 90];
const COVERAGE_DOMAIN_MAX = 200;

export const buildCoverage = ({ windowRows, current, currentIdx, categoryName, resolveProduct }) => {
  const idealMins = windowRows.map((row) => row.idealCoverageMin).filter(Boolean);
  const idealMaxs = windowRows.map((row) => row.idealCoverageMax).filter(Boolean);

  const idealRange = idealMins.length && idealMaxs.length
    ? [Math.round(sum(idealMins, (v) => v) / idealMins.length), Math.round(sum(idealMaxs, (v) => v) / idealMaxs.length)]
    : FALLBACK_IDEAL_COVERAGE;

  const ranked = windowRows
    .filter((row) => row.idx === currentIdx && row.inventory !== null)
    .map((row) => {
      const { name, categoria } = resolveProduct(row);
      const days = simulateCoverageDays(row.inventory, row.salesProfile, row.idx);

      return {
        id: row.productId,
        name,
        categoria,
        dias: Math.round(days),
        atCap: days >= COVERAGE_HORIZON_DAYS,
        capital: row.inventoryCurrency,
      };
    });

  return {
    categoryName,
    cobertura: current.cobertura,
    giro: current.giroAtual,
    idealRange,
    domainMax: COVERAGE_DOMAIN_MAX,
    lowestCoverage: [...ranked].sort((a, b) => a.dias - b.dias).slice(0, 5),
    highestCoverage: [...ranked].sort((a, b) => b.dias - a.dias).slice(0, 5),
  };
};

export const aggregateSellThrough = (rows, convert) =>
  optionalWeightedAvg(rows, (row) => convert(row.sellThrough), (row) => row.realized);